import React, { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'

import { adminFeedbackApi, type AdminListResponse } from '../../../services/feedbackApi'
import {
  FEEDBACK_IMPACT_OPTIONS,
  FEEDBACK_STATUS_META,
  FEEDBACK_STATUS_OPTIONS,
  FEEDBACK_TYPE_OPTIONS,
  type AdminFeedbackSummary,
  type FeedbackImpact,
  type FeedbackStatus,
  type FeedbackType,
} from '../../../types/feedback'
import { formatRelativeTimestamp } from '../../../utils/dateFormatters'
import AdminPageHeader from '../../../components/admin/shared/AdminPageHeader'
import BaseDataTable, { TableColumn } from '../../../components/shared/BaseDataTable'
import { useDataFetching } from '../../../hooks/useDataFetching'
import { useListPageState } from '../../../hooks/useListPageState'
import FeedbackDetailDialog from './FeedbackDetailDialog'

function labelFor(value: string, options: { value: string; label: string }[]): string {
  return options.find(o => o.value === value)?.label || value
}

const FeedbackAdminPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const dateFilter = searchParams.get('date')

  // Deep-link: ?id=<feedbackId> opens that row's detail dialog on load
  // (e.g. the "New feedback received" admin notification links here).
  const idParam = searchParams.get('id')
  const initialId = idParam && /^\d+$/.test(idParam) ? Number(idParam) : null

  const [selectedId, setSelectedId] = useState<number | null>(initialId)

  // Filters + pagination (1-based). URL sync off so it doesn't strip the ?date deep-link param.
  const { state, updateField } = useListPageState({
    defaultPerPage: 50,
    syncWithURL: false,
    initialState: { status: '', type: '', impact: '', fpCode: '' },
  })

  const fetchFeedback = useCallback(
    () =>
      adminFeedbackApi.list({
        status: (state.status as FeedbackStatus) || undefined,
        type: (state.type as FeedbackType) || undefined,
        impact: (state.impact as FeedbackImpact) || undefined,
        fp_code: (state.fpCode as string)?.trim() || undefined,
        search: (state.search as string)?.trim() || undefined,
        page: state.page,
        page_size: state.perPage,
      }),
    [state.status, state.type, state.impact, state.fpCode, state.search, state.page, state.perPage],
  )

  const { data, loading, error, refetch, setData } = useDataFetching<AdminListResponse>({
    fetchFn: fetchFeedback,
    dependencies: [
      state.status,
      state.type,
      state.impact,
      state.fpCode,
      state.search,
      state.page,
      state.perPage,
      dateFilter,
    ],
  })

  // Client-side "today" narrow when ?date=today is set so the SystemStatus tile
  // deep-link lands on today's submissions. Pagination total stays the server total.
  const rows = useMemo(() => {
    const items = data?.items ?? []
    if (dateFilter === 'today') {
      const today = new Date().toISOString().slice(0, 10)
      return items.filter(r => r.created_at.startsWith(today))
    }
    return items
  }, [data, dateFilter])

  const handleUpdated = (updated: AdminFeedbackSummary) => {
    if (!data) return
    setData({
      ...data,
      items: data.items.map(r => (r.id === updated.id ? { ...r, ...updated } : r)),
    })
  }

  const columns: TableColumn<AdminFeedbackSummary>[] = [
    {
      key: 'created_at',
      label: 'When',
      render: (row) => {
        const ts = formatRelativeTimestamp(row.created_at)
        return (
          <Tooltip title={ts.title}>
            <time dateTime={ts.dateTime}>{ts.label}</time>
          </Tooltip>
        )
      },
    },
    { key: 'user', label: 'User', render: (row) => row.user_email || `#${row.user_id}` },
    { key: 'fp_code', label: 'FP', render: (row) => row.fp_code || '-' },
    { key: 'type', label: 'Type', render: (row) => labelFor(row.type, FEEDBACK_TYPE_OPTIONS) },
    { key: 'impact', label: 'Impact', render: (row) => labelFor(row.impact, FEEDBACK_IMPACT_OPTIONS) },
    {
      key: 'title',
      label: 'Title',
      render: (row) => (
        <Typography
          variant="body2"
          sx={{ maxWidth: 320, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {row.title || '-'}
        </Typography>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => {
        const meta = FEEDBACK_STATUS_META[row.status]
        return (
          <Chip
            size="small"
            variant="outlined"
            label={meta?.label ?? row.status}
            sx={{
              fontWeight: 600,
              color: meta?.color,
              borderColor: meta?.color,
              backgroundColor: '#fff',
            }}
          />
        )
      },
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: (row) => (
        <Tooltip title="View / edit">
          <IconButton
            size="small"
            aria-label="View feedback"
            onClick={(e) => {
              e.stopPropagation()
              setSelectedId(row.id)
            }}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ]

  return (
    <>
      <AdminPageHeader
        pageKey="feedback"
        actions={
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} useFlexGap flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={state.status || ''}
              onChange={e => updateField('status', e.target.value)}
            >
              <MenuItem value="">Any</MenuItem>
              {FEEDBACK_STATUS_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Type</InputLabel>
            <Select
              label="Type"
              value={state.type || ''}
              onChange={e => updateField('type', e.target.value)}
            >
              <MenuItem value="">Any</MenuItem>
              {FEEDBACK_TYPE_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Impact</InputLabel>
            <Select
              label="Impact"
              value={state.impact || ''}
              onChange={e => updateField('impact', e.target.value)}
            >
              <MenuItem value="">Any</MenuItem>
              {FEEDBACK_IMPACT_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="FP code"
            value={state.fpCode || ''}
            onChange={e => updateField('fpCode', e.target.value)}
            sx={{ width: 130 }}
          />
          <TextField
            size="small"
            label="Search"
            value={state.search}
            onChange={e => updateField('search', e.target.value)}
            sx={{ flex: 1, minWidth: 200 }}
          />
        </Stack>
      </Paper>

      <BaseDataTable
        variant="enhanced"
        columns={columns}
        data={rows}
        loading={loading}
        error={error}
        showCount
        totalCount={data?.total ?? 0}
        countLabel="feedback"
        emptyMessage="No feedback matches these filters."
        onRowClick={(row) => setSelectedId(row.id)}
        getRowKey={(row) => row.id}
        pagination={{
          page: state.page,
          perPage: state.perPage,
          total: data?.total ?? 0,
          onPageChange: (p) => updateField('page', p),
        }}
      />

      <FeedbackDetailDialog
        feedbackId={selectedId}
        open={selectedId !== null}
        onClose={() => setSelectedId(null)}
        onUpdated={handleUpdated}
      />
    </>
  )
}

export default FeedbackAdminPage
